/** Логика рамки Eddy «Сними удачный кадр» (2D, сегменты random ↔ toward). */

export type EddyVec2 = { readonly x: number; readonly y: number };

export type EddySegmentKind = 'random' | 'toward';

export type EddyBounds = {
  readonly minX: number;
  readonly maxX: number;
  readonly minY: number;
  readonly maxY: number;
};

export type EddySizedPos = EddyVec2 & {
  readonly w: number;
  readonly h: number;
};

export type EddyAdvanceResult = {
  readonly pos: EddyVec2;
  readonly vel: EddyVec2;
  readonly bounced: boolean;
};

/** Длительность одного сегмента направления (мс). */
export const EDDY_FRAME_SEGMENT_MS = 600 as const;

/** Скорость центра рамки в % фото-зоны в секунду. */
export const EDDY_FRAME_SPEED_PCT_PER_SEC = 38 as const;

/** Размеры в % фото-зоны (центр + ширина/высота). Мем и рамка ×1.25 к базе. */
export const EDDY_FRAME_WIDTH_PCT = 45 as const;
export const EDDY_FRAME_HEIGHT_PCT = 56.25 as const;
export const EDDY_MEME_WIDTH_PCT = 35 as const;
export const EDDY_MEME_HEIGHT_PCT = 43.75 as const;

export function nextEddySegmentKind(prev: EddySegmentKind): EddySegmentKind {
  return prev === 'random' ? 'toward' : 'random';
}

export function eddyRandomUnitVector(random: () => number = Math.random): EddyVec2 {
  const angle = random() * Math.PI * 2;
  return { x: Math.cos(angle), y: Math.sin(angle) };
}

export function eddyVectorToward(from: EddyVec2, to: EddyVec2): EddyVec2 {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy);
  if (len < 1e-6) {
    return { x: 1, y: 0 };
  }
  return { x: dx / len, y: dy / len };
}

function isEddyVelocityInward(pos: EddyVec2, vel: EddyVec2, bounds: EddyBounds): boolean {
  if (pos.x <= bounds.minX && vel.x < 0) {
    return false;
  }
  if (pos.x >= bounds.maxX && vel.x > 0) {
    return false;
  }
  if (pos.y <= bounds.minY && vel.y < 0) {
    return false;
  }
  if (pos.y >= bounds.maxY && vel.y > 0) {
    return false;
  }
  return true;
}

/** Вектор сегмента; при отскоке гарантирует направление внутрь сцены. */
export function eddyVelocityForSegment(
  kind: EddySegmentKind,
  from: EddyVec2,
  target: EddyVec2,
  bounds: EddyBounds | null = null,
  random: () => number = Math.random,
): EddyVec2 {
  const primary =
    kind === 'toward' ? eddyVectorToward(from, target) : eddyRandomUnitVector(random);
  if (!bounds || isEddyVelocityInward(from, primary, bounds)) {
    return primary;
  }
  for (let i = 0; i < 12; i += 1) {
    const candidate = eddyRandomUnitVector(random);
    if (isEddyVelocityInward(from, candidate, bounds)) {
      return candidate;
    }
  }
  const midX = (bounds.minX + bounds.maxX) / 2;
  const midY = (bounds.minY + bounds.maxY) / 2;
  return eddyVectorToward(from, { x: midX, y: midY });
}

export function eddyFrameBounds(
  frameW: number = EDDY_FRAME_WIDTH_PCT,
  frameH: number = EDDY_FRAME_HEIGHT_PCT,
): EddyBounds {
  return {
    minX: frameW / 2,
    maxX: 100 - frameW / 2,
    minY: frameH / 2,
    maxY: 100 - frameH / 2,
  };
}

/**
 * Случайная позиция мема: полностью на сцене и достижима рамкой
 * (центр мема внутри bounds рамки — иначе угол не поймать).
 */
export function eddyRandomMemePosition(
  memeW: number = EDDY_MEME_WIDTH_PCT,
  memeH: number = EDDY_MEME_HEIGHT_PCT,
  random: () => number = Math.random,
  frameW: number = EDDY_FRAME_WIDTH_PCT,
  frameH: number = EDDY_FRAME_HEIGHT_PCT,
): EddyVec2 {
  const frameBounds = eddyFrameBounds(frameW, frameH);
  const minX = Math.max(memeW / 2, frameBounds.minX);
  const maxX = Math.min(100 - memeW / 2, frameBounds.maxX);
  const minY = Math.max(memeH / 2, frameBounds.minY);
  const maxY = Math.min(100 - memeH / 2, frameBounds.maxY);
  const spanX = Math.max(0, maxX - minX);
  const spanY = Math.max(0, maxY - minY);
  return {
    x: minX + random() * spanX,
    y: minY + random() * spanY,
  };
}

export function advanceEddyFrame(
  pos: EddyVec2,
  vel: EddyVec2,
  dtSec: number,
  speedPctPerSec: number,
  bounds: EddyBounds,
  segmentKind: EddySegmentKind,
  target: EddyVec2,
  random: () => number = Math.random,
): EddyAdvanceResult {
  const next = {
    x: pos.x + vel.x * speedPctPerSec * dtSec,
    y: pos.y + vel.y * speedPctPerSec * dtSec,
  };
  const hitEdge =
    next.x >= bounds.maxX ||
    next.x <= bounds.minX ||
    next.y >= bounds.maxY ||
    next.y <= bounds.minY;
  if (!hitEdge) {
    return { pos: next, vel, bounced: false };
  }
  const clamped = {
    x: Math.min(bounds.maxX, Math.max(bounds.minX, next.x)),
    y: Math.min(bounds.maxY, Math.max(bounds.minY, next.y)),
  };
  const bouncedVel = eddyVelocityForSegment(segmentKind, clamped, target, bounds, random);
  return { pos: clamped, vel: bouncedVel, bounced: true };
}

/** Полное AABB-вхождение мема в рамку (центры + размеры в одних единицах). */
export function isEddyMemeInsideFrame(meme: EddySizedPos, frame: EddySizedPos): boolean {
  const memeLeft = meme.x - meme.w / 2;
  const memeRight = meme.x + meme.w / 2;
  const memeTop = meme.y - meme.h / 2;
  const memeBottom = meme.y + meme.h / 2;
  const frameLeft = frame.x - frame.w / 2;
  const frameRight = frame.x + frame.w / 2;
  const frameTop = frame.y - frame.h / 2;
  const frameBottom = frame.y + frame.h / 2;
  return (
    memeLeft >= frameLeft &&
    memeRight <= frameRight &&
    memeTop >= frameTop &&
    memeBottom <= frameBottom
  );
}
